'use client';
import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

const MULTI_VALUE_FILTER_KEYS = [
  'categories',
  'grillSeries',
  'color',
  'apparelFit',
  'apparelSize',
  'features',
  'grillCompatibility',
  'pairings',
  'relatedCategories',
  'cookingMethod',
  'mealType',
  'hardwood',
  'holiday',
  'diet',
  'tags',
  'featuredContent',
  'featured',
  'priceGroup',
  'learnCategories',
];

export const SINGLE_VALUE_FILTER_KEYS = ['complexity'];

const ALL_FILTER_KEYS = [...MULTI_VALUE_FILTER_KEYS, ...SINGLE_VALUE_FILTER_KEYS, 'sort'];

interface UseSearchUrlStateArgs {
  /** Term used when the URL has none; also seeded into the URL once */
  defaultSearchTerm?: string;
  /** Ref to the search input; its current value is committed on every URL mutation */
  inputRef: RefObject<HTMLInputElement | null>;
}

/**
 * Owns all search state that lives in the URL: term, type, page and filters.
 * Every mutation pushes a new history entry, commits the term currently typed
 * in the search box, and resets pagination.
 *
 * @param args - Hook arguments
 * @param args.defaultSearchTerm - Fallback search term when the URL has none
 * @param args.inputRef - Ref attached to the search input
 * @returns Derived URL state and handlers to mutate it
 */
export const useSearchUrlState = ({ defaultSearchTerm, inputRef }: UseSearchUrlStateArgs) => {
  const { query } = useCurrentUrl();
  const searchTerm = query?.get('term') || defaultSearchTerm || '';
  const searchType = query?.get('type') ?? '';
  const page = query?.get('page') ?? '1';

  // Seed the URL with the default term once. replaceState bypasses Next.js so
  // query.get('term') stays null; the ref prevents the effect from re-running.
  const defaultTermAppliedRef = useRef(false);
  useEffect(() => {
    if (!query?.get('term') && defaultSearchTerm && !defaultTermAppliedRef.current) {
      defaultTermAppliedRef.current = true;
      const url = new URL(window.location.href);
      url.searchParams.set('term', defaultSearchTerm);
      window.history.replaceState(null, '', url);
    }
  }, [defaultSearchTerm, query]);

  const activeFilters = useMemo((): Record<string, Array<string>> => {
    const multi = MULTI_VALUE_FILTER_KEYS.map((key) => [key, query?.getAll(key) ?? []] as const);
    const single = SINGLE_VALUE_FILTER_KEYS.map(
      (key) => [key, [query?.get(key) ?? ''].filter(Boolean)] as const,
    );
    return Object.fromEntries([...multi, ...single].filter(([, values]) => values.length));
  }, [query]);

  /**
   * Pushes a new history entry after applying a mutation to the current URL params.
   *
   * @param mutate - Receives the params to modify in place
   */
  const pushParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const url = new URL(window.location.href);
      const typedTerm = inputRef.current?.value?.trim();
      if (typedTerm) {
        url.searchParams.set('term', typedTerm);
      }
      mutate(url.searchParams);
      url.searchParams.delete('page');
      window.history.pushState(null, '', url);
    },
    [inputRef],
  );

  const setSearchTerm = useCallback(
    (term: string) => {
      if (!term.trim()) return;
      pushParams((params) => params.set('term', term));
    },
    [pushParams],
  );

  const setSearchType = useCallback(
    (type: string) =>
      pushParams((params) => {
        if (type === 'All') {
          params.delete('type');
        } else {
          params.set('type', type);
        }
        ALL_FILTER_KEYS.forEach((key) => params.delete(key));
      }),
    [pushParams],
  );

  /**
   * Turns a filter value on or off. Selecting a value on a single-value filter
   * replaces whatever was selected before; multi-value filters accumulate.
   *
   * @param key - Filter URL param key
   * @param value - Filter value to toggle
   */
  const toggleFilter = useCallback(
    (key: string, value: string) =>
      pushParams((params) => {
        const current = params.getAll(key);
        if (current.includes(value)) {
          params.delete(key);
          current.filter((v) => v !== value).forEach((v) => params.append(key, v));
        } else if (SINGLE_VALUE_FILTER_KEYS.includes(key)) {
          params.set(key, value);
        } else {
          params.append(key, value);
        }
      }),
    [pushParams],
  );

  const clearAllFilters = useCallback(
    () =>
      pushParams((params) => {
        params.delete('type');
        ALL_FILTER_KEYS.forEach((key) => params.delete(key));
      }),
    [pushParams],
  );

  return {
    searchTerm,
    searchType,
    page,
    activeFilters,
    setSearchTerm,
    setSearchType,
    toggleFilter,
    clearAllFilters,
  };
};
